import { useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, Switch, TextField, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { T, stitchDialogPaper, stitchGradientBtn, stitchOutlineBtn } from '../ProjectOverview/parts/stitch';

/**
 * Fasen aan/uit zetten, status handmatig overriden en een eigen fase toevoegen.
 * Uitzetten is omkeerbaar (is_enabled=false op de server, geen delete).
 */
function PhaseSettingsDialog({ open, onClose, projectRecId, phases, driftKeys = [], onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const [nieuweFase, setNieuweFase] = useState('');

  const patchPhase = async (phase, body) => {
    setBusyId(phase.id);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/phases/${phase.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Bijwerken mislukt');
      onChanged();
    } catch (e) { toast.error(e.message); }
    finally { setBusyId(null); }
  };

  const addPhase = async (label) => {
    const naam = String(label || '').trim();
    if (!naam) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/phases`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ label: naam }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Toevoegen mislukt');
      setNieuweFase('');
      onChanged();
      toast.success(`Fase "${naam}" toegevoegd`);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: stitchDialogPaper }}>
      <DialogTitle sx={{ fontFamily: T.font, fontWeight: 800 }}>Fasen van dit project</DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, display: 'block', mb: 1.5 }}>
          Niet elke productie doorloopt alles — zet fasen uit die hier niet spelen (de balk
          wordt korter) en zet ze gerust weer aan: er gaat niets verloren.
        </Typography>
        {phases.map((p) => (
          <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75,
            borderBottom: `1px solid ${T.surfaceLow}`, opacity: busyId === p.id ? 0.5 : 1 }}>
            <FormControlLabel sx={{ flex: 1, mr: 0 }}
              control={<Switch size="small" checked={p.is_enabled}
                onChange={(e) => patchPhase(p, { is_enabled: e.target.checked })} />}
              label={<Typography variant="body2" sx={{ fontFamily: T.fontBody, fontWeight: 600 }}>{p.label}</Typography>} />
            <TextField select size="small" value={p.status_override || ''} sx={{ width: 170 }}
              label="Status" onChange={(e) => patchPhase(p, { status_override: e.target.value })}>
              <MenuItem value="">Automatisch ({p.status})</MenuItem>
              <MenuItem value="todo">Nog niet</MenuItem>
              <MenuItem value="bezig">Bezig</MenuItem>
              <MenuItem value="klaar">Klaar</MenuItem>
            </TextField>
          </Box>
        ))}

        {driftKeys.length > 0 && (
          <Box sx={{ mt: 2, p: 1.5, borderRadius: '0.6rem', bgcolor: '#fffbeb', border: '1px solid #fde68a' }}>
            <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: '#92400e', fontWeight: 700, display: 'block', mb: 0.75 }}>
              Nieuwe fase(n) beschikbaar in de templates — toevoegen aan dit project?
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {driftKeys.map((k) => (
                <Button key={k} size="small" onClick={() => addPhase(k)}
                  sx={{ ...stitchOutlineBtn, fontSize: '0.75rem' }}>+ {k}</Button>
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <TextField size="small" fullWidth label="Eigen fase toevoegen" placeholder="Bijv. Vertalingen"
            value={nieuweFase} onChange={(e) => setNieuweFase(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPhase(nieuweFase); } }} />
          <Box component="span" onClick={() => addPhase(nieuweFase)}
            sx={{ ...stitchGradientBtn, opacity: nieuweFase.trim() ? 1 : 0.5 }}>
            Toevoegen
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Box component="span" onClick={onClose} sx={stitchOutlineBtn}>Sluiten</Box>
      </DialogActions>
    </Dialog>
  );
}

export default PhaseSettingsDialog;
